import { useMemo } from 'react';

import { useInvalidate } from '@refinedev/core';

import { Button, Loading, useSnackbar } from '@rrexpress/core';
import { type ColumnDef } from '@tanstack/react-table';
import Cookies from 'js-cookie';

import ActionDelete from '@/components/action-button/delete';
import ActionUpdate from '@/components/action-button/update';
import BaseList from '@/components/base-list';
import ChipStatus from '@/components/chip-status';
import useLayout from '@/hooks/useLayout';
import { useModal } from '@/hooks/useModal';
import useUser from '@/modules/user/application/user';

import UserManagementCreate from './create';
import UserManagementUpdate from './update';

const UserManagement = () => {
  useLayout({ title: 'Manajemen User' });
  const { open, close } = useModal();
  const { deleteUser, loadingDelete } = useUser();
  const { show } = useSnackbar();
  const invalidate = useInvalidate();
  const userLoginData = JSON.parse(Cookies.get('user_login') as string);

  async function handleOnDelete(id: string, rowVersion: string) {
    try {
      await deleteUser(id, rowVersion);
      close();
      show('Berhasil menghapus user', 'success');
      invalidate({ resource: 'cp-users', invalidates: ['list'] });
    } catch (error) {
      show('Gagal menghapus user', 'error');
    }
  }

  const columns: ColumnDef<IUser>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Nama',
      accessorKey: 'full_name',
      // cell: ({ row }) => (
      //   <Link to={`/user-management/show/${row.original.user_id}`}>
      //     <span className="text-blue-500">{row.original.full_name}</span>
      //   </Link>
      // ),
    },
    {
      id: 'role',
      accessorKey: 'role_name',
      header: 'Role',
    },
    {
      id: 'phone',
      accessorKey: 'phone_number',
      header: 'No. Telepon',
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'Email',
    },
    {
      id: 'branch',
      accessorKey: 'branch_name',
      header: 'Cabang',
      cell: ({ getValue }) => getValue() as string || '-',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => <ChipStatus status={getValue() as string} />,
    },
    {
      id: 'id',
      accessorKey: 'user_id',
      header: 'Action',
      cell: ({ getValue, row }) => (
        !row.original.is_owner ? <div className="flex gap-2 mx-auto">
          <ActionUpdate onClick={() => open({
            template: <UserManagementUpdate data={row.original} />,
            title: 'Edit User',
            footer: false,
            width: 500,
          })} />

          {userLoginData.userId !== getValue() && (
            <ActionDelete onClick={() => open({
              title: 'Hapus User',
              template: loadingDelete
                ? (
                  <Loading
                    variant="circular"
                    size={40}
                    speed={0.6}
                    indicatorCap="round"
                  />
                )
                : (
                  <p className="text-[#222222]">
                    Apakah anda yakin akan menghapus <span className="font-medium">{row?.original?.full_name}</span> dari daftar user?
                  </p>
                ),
              footer: (
                <div className="flex gap-3">
                  <Button variant="text" onClick={() => close()}>Batal</Button>
                  <Button backgroundColor="#BE321E" onClick={() => handleOnDelete(getValue() as string, row.original.row_version)}>
                    Hapus
                  </Button>
                </div>
              ),
            })} />
          )}
        </div> : <></>
      ),
    },
  ], []);

  return (
    <BaseList
      title="Daftar User"
      resource="cp-users"
      columns={columns as any}
      filter={{
        onSubmit: (val) => ({
          ...val,
          full_name: val.type === 'full_name' && val.keyword ? val.keyword : null,
          user_name: val.type === 'user_name' && val.keyword ? val.keyword : null,
          status: val.status === 'all' ? undefined : val.status,
        }),
        sections: [
          {
            layout: {
              direction: 'horizontal',
              sections: [
                {
                  input: {
                    name: 'type',
                    type: 'dropdown',
                    label: 'Type',
                    comboboxOptions: [
                      { label: 'Nama', value: 'full_name' },
                      { label: 'Email', value: 'user_name' },
                    ],
                  },
                },
                {
                  input: {
                    name: 'keyword',
                    type: 'textfield',
                    label: 'Keyword',
                  },
                },
                {
                  input: {
                    name: 'status',
                    type: 'dropdown',
                    label: 'Status',
                    defaultValue: 'all',
                    comboboxOptions: [
                      { label: 'Semua', value: 'all' },
                      { label: 'Aktif', value: 'active' },
                      { label: 'Tidak Aktif', value: 'nonactive' },
                      { label: 'Pending', value: 'pending' },
                    ],
                  },
                },
              ],
            },
          },
        ],
      }}
      headerActions={[{
        type: 'tertiary',
        label: 'Invite User',
        onClick: () => open({
          template: <UserManagementCreate />,
          title: 'Undang User',
          footer: false,
          width: 500,
        }),
      }]}
    />
  );
};

export default UserManagement;
